import type { ColumnDef, PaginationState } from '@tanstack/react-table';
import {
    Box,
    Icon,
    Link,
    Pagination,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    Tooltip,
} from '@ksyos/design-system';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import type { PractitionerDataError } from '@common/interfaces/api/practitionerDataErrorsApi';
import { getDisplayName } from '@common/utils/getDisplayName';
import { NullableFormattedDatetime } from '@frontend/components/elements/NullableFormattedDatetime';
import { FilterRow } from '@frontend/components/elements/tanStackFilter/FilterRow';
import { dateAfterFilter } from '@frontend/components/elements/tanStackFilter/utils/dateAfterFilter';
import { getSortDirection } from '@frontend/components/elements/tanStackFilter/utils/getSortDirection';
import { getSelectedLanguage } from '@frontend/translate';
import { usePersistentTableState } from '@frontend/utils/hooks/usePersistedTableState';
import ErrorDetailsCell from './ErrorDetailsCell';
import ErrorTypeCell from './ErrorTypeCell';
import PractitionerIdCell from './PractitionerIdCell';
import PractitionerTableRow from './PractitionerTableRow';

const columnHelper = createColumnHelper<PractitionerDataError>();

type PractitionerChecksProps = { items: PractitionerDataError[] };

export default function PractitionerChecksTable({ items }: Readonly<PractitionerChecksProps>) {
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });

    const translate = getSelectedLanguage();

    const columns: ColumnDef<PractitionerDataError, any>[] = [
        columnHelper.accessor('practitioner.uid', {
            cell: ({ row }) => <PractitionerIdCell row={row} />,
            enableSorting: true,
            header: translate.dataChecks.id,
        }),
        {
            accessorFn: ({ practitioner: { nameFamily, nameGiven, nameInfix } }) =>
                getDisplayName(
                    {
                        nameFamily,
                        nameGiven,
                        nameInfix,
                    },
                    false,
                    'familyNameCommaFirstName'
                ),
            cell: ({
                row: {
                    original: {
                        practitioner: { nameFamily, nameGiven, nameInfix },
                    },
                },
            }) =>
                getDisplayName(
                    {
                        nameFamily,
                        nameGiven,
                        nameInfix,
                    },
                    false,
                    'familyNameCommaFirstName'
                ),
            enableSorting: true,
            header: translate.dataChecks.name,
        },
        {
            accessorFn: (row) => `${translate.practitioner.practitionerDataErrorType[row.type]}`,
            cell: ({ row }) => <ErrorTypeCell row={row} />,
            enableSorting: true,
            filterFn: 'arrIncludesSome',
            header: () => (
                <>
                    {translate.dataChecks.checkType}
                    <Tooltip content={translate.dataChecks.typeTooltip}>
                        <Icon
                            icon="information"
                            sx={{ ml: 2 }}
                        />
                    </Tooltip>
                </>
            ),
            id: translate.dataChecks.checkType,
            meta: {
                filterVariant: 'select',
            },
        },
        columnHelper.accessor('created_at', {
            cell: ({ row }) => <NullableFormattedDatetime value={row.original.created_at} />,
            enableSorting: true,
            filterFn: dateAfterFilter,
            header: translate.dataChecks.checkDate,
            meta: {
                filterVariant: 'dateAfter',
            },
        }),
        columnHelper.accessor('details', {
            cell: ({ row }) => (
                <ErrorDetailsCell
                    details={row.original.details}
                    type={row.original.type}
                />
            ),
            enableColumnFilter: false,
            enableSorting: false,
            header: translate.dataChecks.checkDetails,
        }),
    ];

    const defaultSorting = useMemo(() => [{ desc: true, id: 'created_at' }], []);

    const { columnFilters, setColumnFilters, sorting, setSorting } = usePersistentTableState({
        defaultSorting,
        storageKeyPrefix: 'practitionerChecksTable',
    });

    const { setPageIndex, setPageSize, getHeaderGroups, getRowModel, getPrePaginationRowModel } = useReactTable({
        columns,
        data: items,
        getCoreRowModel: getCoreRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        state: {
            columnFilters,
            pagination,
            sorting,
        },
    });

    return (
        <>
            <Box
                display="flex"
                justifyContent="flex-end"
                margin={4}
            >
                <Link
                    onClick={() => setColumnFilters([])}
                    variant="button"
                >
                    {translate.common.clearFilters}
                </Link>
            </Box>
            <TableContainer>
                <Table
                    spacing="medium"
                    style={{ borderCollapse: 'collapse' }}
                >
                    <TableHead>
                        {getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHeader
                                        isSortable={header.column.getCanSort()}
                                        isSortHeader={!!header.column.getIsSorted()}
                                        key={header.id}
                                        onClick={header.column.getToggleSortingHandler()}
                                        sortDirection={getSortDirection(header)}
                                        style={{ padding: 0 }}
                                    >
                                        {!header.isPlaceholder &&
                                            flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHeader>
                                ))}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody>
                        <FilterRow headerGroups={getHeaderGroups()} />
                        {getRowModel().rows.map((row) => (
                            <PractitionerTableRow
                                key={row.id}
                                row={row}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {getPrePaginationRowModel().rows.length > pagination.pageSize && (
                <Pagination
                    onChange={({ page: newPage, pageSize: newPageSize }) => {
                        setPageIndex(newPage - 1);
                        setPageSize(newPageSize);
                    }}
                    page={pagination.pageIndex + 1}
                    pageSize={pagination.pageSize}
                    totalItems={getPrePaginationRowModel().rows.length}
                />
            )}
        </>
    );
}
